import React, { useState } from "react";

import useD3 from "../util/use-d3";
import { scaleLinear, axisBottom, axisLeft, bisector, min, max, pointer, format, line } from "d3";
import { css } from "@emotion/react";
import { DownloadsLabel } from "../shared/DownloadsLabel";

const margin = { top: 15, right: 5, bottom: 50, left: 45 };

const tipNumberFormat = format(",");

export default function EpisodeGrowthChart({ data, colors, height = "30vh", days = 7 }) {
    const [elemRef, dimensionRef] = useD3(
        (svg, { width, height }) => {
            const w = width - margin.left - margin.right,
                h = height - margin.top - margin.bottom;

            const xScale = scaleLinear()
                .domain([1, days])
                .range([0, w]);

            const xAxis = axisBottom(xScale).ticks(days);

            const valueDomain = data.length
                ? [
                      min(data, d => d.days[0].cumulativeDownloads),
                      max(data, d => max(d.days, day => day.cumulativeDownloads))
                  ]
                : [0, 1000];

            const yScale = scaleLinear()
                .domain(valueDomain)
                .range([h, 0]);

            const downloadLine = line()
                .x((d, i) => xScale(i + 1))
                .y(d => yScale(d.cumulativeDownloads))
                .defined(d => typeof d.cumulativeDownloads == "number");

            const yAxis = axisLeft(yScale)
                .ticks(4)
                .tickFormat(format("~s"));

            const chartArea = svg.select(".chart-area");
            chartArea.attr("transform", `translate(${margin.left},${margin.top})`);

            svg.select(".x-axis").attr("transform", `translate(${margin.left},${margin.top + h})`);
            svg.select(".y-axis").attr("transform", `translate(${margin.left},${margin.top})`);
            svg.select(".xlab").attr("transform", `translate(${width / 2},${margin.top + h + 35})`);

            chartArea
                .select(".lines")
                .selectAll(".line")
                .data(data)
                .join("path")
                .attr("class", "line")
                .attr("fill", "none")
                .attr("stroke", d => colors(d.episodeTitle))
                .attr("stroke-width", 5)
                .attr("stroke-opacity", 0.85)
                .attr("d", d => downloadLine(d.days));

            svg.select(".x-axis").call(xAxis);
            svg.select(".y-axis").call(yAxis);

            const [x1, x2] = xScale.range();
            svg
                .select(".grid")
                .selectAll("line.grid-y")
                .data(yScale.ticks(4))
                .join("line")
                .attr("class", "grid-y")
                .attr("x1", x1)
                .attr("y1", yScale)
                .attr("x2", x2)
                .attr("y2", yScale)
                .attr("stroke", "#eee");

            const [y1, y2] = yScale.range();
            svg
                .select(".grid")
                .selectAll("line.grid-x")
                .data(xScale.ticks(30))
                .join("line")
                .attr("class", "grid-x")
                .attr("x1", xScale)
                .attr("y1", y1)
                .attr("x2", xScale)
                .attr("y2", y2)
                .attr("stroke", "#eee");

            const pointerX = svg
                .select(".pointerX")
                .attr("stroke", "#eee")
                .attr("stroke-dasharray", "4, 2")
                .attr("d", `M 0 0 V ${h}`)
                .style("display", "none");
            const pointerY = svg
                .select(".pointerY")
                .attr("stroke", "#eee")
                .attr("stroke-dasharray", "4, 2")
                .attr("d", `M 0 0 H ${w}`)
                .style("display", "none");

            const ow = 230,
                oh = (data.length + 2) * 15;
            const toolTip = chartArea.select(".toolTip").style("display", "none");
            const dot = chartArea.select(".dot").style("display", "none");
            const tipDate = toolTip.select(".date");
            const tipName = toolTip.select(".name").text(name);
            const tipCurrent = toolTip.select(".current");
            //const tipPrevious = toolTip.select(".previous");
            toolTip
                .select(".tipBg")
                .attr("width", ow)
                .attr("height", oh);

            function mouseout() {
                pointerX.style("display", "none");
                pointerY.style("display", "none");
                toolTip.style("display", "none");
                dot.style("display", "none");
                //setState(state => ({ ...state, tipVisible: false }));
            }

            function mouseover() {}

            function mousemove(event) {
                const p = pointer(event),
                    x0 = xScale.invert(p[0]),
                    mouseDay = Math.round(x0);
                //console.log({ p, x0, i });
                if (mouseDay >= 1 && mouseDay <= days) {
                    toolTip.select(".day").text(`Day ${mouseDay}`);
                    toolTip
                        .selectAll(".series")
                        .data(data)
                        .join("rect")
                        .attr("class", "series")
                        .attr("fill", d => colors(d.episodeTitle))
                        .attr("width", 10)
                        .attr("height", 10)
                        .attr("x", 20)
                        .attr("y", (d, i) => i * 15 + 25);

                    toolTip
                        .selectAll(".downloads")
                        .data(data)
                        .join("text")
                        .attr("class", "downloads")
                        .attr("width", 10)
                        .attr("height", 10)
                        .attr("dx", 120)
                        .attr("text-anchor", "end")
                        .attr("y", (d, i) => i * 15 + 34)
                        .text(
                            d =>
                                typeof d.days[mouseDay - 1].downloads == "number"
                                    ? tipNumberFormat(d.days[mouseDay - 1].downloads)
                                    : "-"
                        );

                    toolTip
                        .selectAll(".cumulative")
                        .data(data)
                        .join("text")
                        .attr("class", "cumulative")
                        .attr("width", 10)
                        .attr("height", 10)
                        .attr("dx", 220)
                        .attr("text-anchor", "end")
                        .attr("y", (d, i) => i * 15 + 34)
                        .text(
                            d =>
                                typeof d.days[mouseDay - 1].downloads == "number"
                                    ? tipNumberFormat(d.days[mouseDay - 1].cumulativeDownloads)
                                    : "-"
                        );

                    const xPos = p[0],
                        yPos = p[1],
                        xTip = Math.max(0, Math.min(xPos - ow / 2, w - ow)),
                        yTip = Math.max(0, Math.min(yPos - oh, h - oh));

                    toolTip.attr("transform", `translate(${xTip}, ${yTip})`).style("display", null);

                    pointerX.attr("transform", `translate(${xPos}, 0)`).style("display", null);
                    pointerY.attr("transform", `translate(0, ${yPos})`).style("display", null);
                } else {
                    mouseout();
                }
            }

            const mouseRect = svg
                .select(".mouse")
                .attr("fill", "none")
                .style("pointer-events", "all")
                .attr("width", w)
                .attr("height", h)
                .attr("transform", `translate(${margin.left},${margin.top})`)
                .on("mouseout", mouseout)
                .on("mouseover", mouseover)
                .on("mousemove", mousemove);
        },
        [data]
    );

    return (
        <div
            ref={dimensionRef}
            style={{
                marginBottom: "1em",
                position: "relative",
                gridColumn: "1 / -1"
            }}
        >
            <svg
                ref={elemRef}
                css={css`
                    display: block;
                    height: ${height};
                    width: 100%;
                    cursor: crosshair;
                    & .axis {
                        color: #777;
                        shape-rendering: crispEdges;
                    }
                    & .xlab {
                        font-size: 11px;
                    }
                    & .toolTip {
                        .date {
                            font-weight: 600;
                            font-size: 12px;
                        }
                        .current {
                            font-weight: 700;
                            font-size: 12px;
                        }
                        .previous {
                            font-size: 12px;
                        }
                        text {
                            fill: black;
                            font-size: 11px;
                        }
                        .tipBg {
                            opacity: 0.9;
                            fill: white;
                            stroke: #eee;
                        }
                    }
                `}
            >
                <g className="chart-area">
                    <g className="grid" />
                    <g className="lines" />
                    <path className="pointerX" />
                    <path className="pointerY" />
                    <g className="toolTip" style={{ display: "none" }}>
                        <rect className="tipBg" />
                        <text dx="10 " y="15" className="day" fontWeight="bold" />
                        <text dx="120" y="15" textAnchor="end" fontWeight="bold">
                            <DownloadsLabel />
                        </text>
                        <text dx="220" y="15" textAnchor="end" fontWeight="bold">
                            Cumulative
                        </text>
                    </g>
                </g>
                <g className="axis y-axis" />
                <g className="axis x-axis" />
                <text className="xlab" textAnchor="middle">
                    Days since publication
                </text>
                <rect className="mouse" />
            </svg>
        </div>
    );
}
